import React, {useCallback, useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {BearerTokenConsumer, getBearerToken} from 'auth/TokensDAO';
import {ActionOrigin, ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PipelineType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import copyToClipboard from 'util/clipboard/copyToClipboard';

/** How frequently to refresh the analysis key */
const REFRESH_TOKEN_INTERVAL_MS = 1000 * 60;

/**
 * Button to copy the Analysis Key (analysisId:accessToken)
 *
 * Auto-refreshes the key every minute, to make sure it is not expired when the user copies it. This is less
 * than the key's TTL (found in benchling/auth/api/tokens.py) so that the key still has a significant TTL
 * by the time the user copies it, since they may want to use it several minutes after copying.
 *
 * The access token is generated on mount rather than lazily on user-click, to avoid the user having to wait
 * for the token to be generated on-click. If the code were `onClick={getBearerToken().then(copyToClipboard)}`
 * and the following happened:
 * 1. `getBearerToken` gets called
 * 2. user tabs away from Benchling
 * 3. `getBearerToken` finishes; `copyToClipboard` gets called
 *
 * `copyToClipboard` would fail, because `copyToClipboard` requires the window has focus to work.
 *
 * Since the analysis key is meant to be copied into a different application, users tabbing away from Benchling
 * while the analysis key is generating is not impossible, so it's better to avoid that situation by generating
 * the token on mount/in the background, and having it immediately available on-click.
 */
export default function CopyAnalysisKeyButton({
  analysisId,
  disabled,
}: {
  analysisId: string;
  disabled?: boolean;
}): JSX.Element {
  // Intentionally do not use Redux state; if the component remounts when the token has expired, we don't want
  // to use that expired token while the new token is being generated in the useOnMount call.
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const updateBearerToken = useCallback(
    () =>
      getBearerToken({bearerTokenConsumer: BearerTokenConsumer.ASSAYS}).then((token) => {
        setAccessToken(token.access_token);
      }),
    [setAccessToken]
  );

  useOnMount(() => {
    updateBearerToken();
  });

  useEffect(() => {
    const interval = setInterval(updateBearerToken, REFRESH_TOKEN_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [updateBearerToken]);

  function handleCopyAnalysisKey(): void {
    assert(accessToken != null, 'Access token not defined');
    copyToClipboard(`${analysisId}:${accessToken}`, {
      showToastOnSuccess: true,
      toastText: 'Copied Analysis key to clipboard.',
    });
    trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.COPIED_ANALYSIS_STEP_KEY, {
      pipeline_id: analysisId,
      pipeline_type: PipelineType.ANALYSIS,
      action_origin: ActionOrigin.ANALYSIS_TOOL,
    });
  }

  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      disabled={accessToken == null || disabled}
      fontWeight={FONT_WEIGHT_PROXIMA_SEMIBOLD}
      icon="copy"
      onClick={handleCopyAnalysisKey}
      size={ButtonSize.SMALL}
      text="Copy Analysis key"
      // Button does not handle overriding textColor if disabled, so do it manually here.
      textColor={accessToken == null || disabled ? undefined : GRAY_DARKER}
      dataTestAttrs={{component: 'CopyAnalysisKeyButton', element: 'root'}}
    />
  );
}
